// 설정 파일 관리 유틸리티
// 프로젝트 루트의 config/ 폴더에 JSON 파일로 설정 저장

const CONFIG_FILE_NAME = 'app-config.json';
const CONFIG_DIR_NAME = 'config';

// 설정 파일 경로 생성
const getConfigFilePath = () => {
  // 개발 환경에서는 public 폴더를 사용 (브라우저에서 접근 가능)
  return `/${CONFIG_DIR_NAME}/${CONFIG_FILE_NAME}`;
};

// 로컬 스토리지 백업 키
const LOCAL_STORAGE_BACKUP_KEY = 'appConfigsBackup';

// 설정 파일에서 설정 불러오기
export const loadConfigFromFile = async () => {
  try {
    const configPath = getConfigFilePath();
    console.log('📁 설정 파일 로드 시도:', configPath);
    
    const response = await fetch(configPath);
    
    if (response.ok) {
      const configData = await response.json();
      console.log('✅ 설정 파일 로드 성공:', configData);
      
      // 유효성 검증
      if (configData.servers && Array.isArray(configData.servers)) {
        // 로컬 스토리지에도 백업 저장
        localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(configData));
        return { success: true, data: configData };
      } else {
        throw new Error('설정 파일 형식이 올바르지 않습니다.');
      }
    } else if (response.status === 404) {
      console.log('📄 설정 파일이 없습니다. 로컬 스토리지에서 백업을 확인합니다.');
      return loadConfigFromLocalStorage();
    } else {
      throw new Error(`설정 파일 로드 실패: ${response.status}`);
    }
  } catch (error) {
    console.warn('⚠️ 설정 파일 로드 실패:', error.message);
    console.log('🔄 로컬 스토리지 백업에서 로드 시도...');
    return loadConfigFromLocalStorage();
  }
};

// 로컬 스토리지에서 백업 설정 불러오기
const loadConfigFromLocalStorage = () => {
  try {
    const backupConfig = localStorage.getItem(LOCAL_STORAGE_BACKUP_KEY);
    if (backupConfig) {
      const configData = JSON.parse(backupConfig);
      console.log('✅ 로컬 스토리지 백업에서 설정 로드 성공');
      return { success: true, data: configData };
    } else {
      console.log('📭 백업 설정도 없습니다. 기본 설정 사용');
      return { success: false, data: null };
    }
  } catch (error) {
    console.error('❌ 로컬 스토리지 백업 로드 실패:', error);
    return { success: false, data: null };
  }
};

// 설정을 파일로 저장 (다운로드 방식)
export const saveConfigToFile = (configData) => {
  try {
    const dataWithTimestamp = {
      ...configData,
      version: "1.0",
      timestamp: new Date().toISOString(),
      lastSaved: new Date().toLocaleString('ko-KR')
    };
    
    // JSON 파일 다운로드
    const dataStr = JSON.stringify(dataWithTimestamp, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = CONFIG_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    // 로컬 스토리지에도 백업 저장
    localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(dataWithTimestamp));
    
    console.log('💾 설정 파일 저장 완료:', CONFIG_FILE_NAME);
    
    return { 
      success: true, 
      message: `설정이 "${CONFIG_FILE_NAME}" 파일로 다운로드되었습니다.\n프로젝트의 public/${CONFIG_DIR_NAME}/ 폴더에 이 파일을 복사하면 자동으로 로드됩니다.` 
    };
  } catch (error) {
    console.error('❌ 설정 파일 저장 실패:', error);
    return { success: false, message: `설정 파일 저장 중 오류: ${error.message}` };
  }
};

// 설정 파일 업로드 처리
export const uploadConfigFile = (file) => {
  return new Promise((resolve) => {
    if (!file || file.type !== 'application/json') {
      resolve({ success: false, message: 'JSON 파일만 업로드 가능합니다.' });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        
        // 유효성 검증
        if (!configData.servers || !Array.isArray(configData.servers)) {
          resolve({ success: false, message: '올바른 설정 파일 형식이 아닙니다.' });
          return;
        }
        
        // 로컬 스토리지에 백업 저장
        localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(configData));
        
        resolve({ 
          success: true, 
          data: configData,
          message: '설정 파일이 성공적으로 업로드되었습니다.\n재시작 시 자동 로드하려면 public/config/ 폴더에 파일을 복사하세요.' 
        });
      } catch (error) {
        resolve({ success: false, message: `파일 파싱 오류: ${error.message}` });
      }
    };
    
    reader.onerror = () => {
      resolve({ success: false, message: '파일 읽기 오류가 발생했습니다.' });
    };
    
    reader.readAsText(file);
  });
};

// 설정을 서버의 public/config/app-config.json 파일에 저장
export const saveConfigToServerFile = async (configData) => {
  try {
    const dataWithTimestamp = {
      ...configData,
      version: "1.0",
      timestamp: new Date().toISOString(),
      lastSaved: new Date().toLocaleString('ko-KR')
    };
    
    console.log('💾 서버 설정 파일 저장 시도...', dataWithTimestamp);
    
    // Node.js 개발 서버에서 파일 시스템 접근을 위한 API 호출
    const response = await fetch('/api/save-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataWithTimestamp)
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ 서버 설정 파일 저장 성공:', result);
      return { success: true, message: '설정이 서버 파일에 저장되었습니다.' };
    } else {
      throw new Error(`서버 응답 오류: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.warn('⚠️ 서버 파일 저장 실패, 로컬 백업만 유지:', error);
    // 서버 파일 저장 실패 시 로컬 백업은 계속 유지
    autoSaveConfigBackup(configData);
    return { success: false, message: `서버 파일 저장 실패: ${error.message}` };
  }
};

// 설정 파일 자동 저장 (로컬 스토리지 백업)
export const autoSaveConfig = async (configData) => {
  try {
    // 1차: 서버 파일에 저장 시도
    const serverSaveResult = await saveConfigToServerFile(configData);
    
    // 2차: 로컬 스토리지 백업은 항상 수행
    const localSaveResult = autoSaveConfigBackup(configData);
    
    console.log('🔄 설정 자동 저장 완료:', {
      serverSave: serverSaveResult.success ? '성공' : '실패',
      localBackup: localSaveResult ? '성공' : '실패'
    });
    
    return serverSaveResult.success || localSaveResult;
  } catch (error) {
    console.error('❌ 설정 자동 저장 실패:', error);
    // 최후의 수단으로 로컬 백업만이라도 저장
    return autoSaveConfigBackup(configData);
  }
};

// 로컬 스토리지 백업만 수행하는 함수
const autoSaveConfigBackup = (configData) => {
  try {
    const dataWithTimestamp = {
      ...configData,
      timestamp: new Date().toISOString(),
      autoSaved: true
    };
    
    localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(dataWithTimestamp));
    console.log('🔄 설정 로컬 백업 저장 완료');
    return true;
  } catch (error) {
    console.error('❌ 설정 로컬 백업 저장 실패:', error);
    return false;
  }
};

// 설정 파일 존재 여부 확인
export const checkConfigFileExists = async () => {
  try {
    const configPath = getConfigFilePath();
    const response = await fetch(configPath, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
};

// 설정 파일 경로 안내
export const getConfigInstructions = () => {
  return {
    filePath: `public/${CONFIG_DIR_NAME}/${CONFIG_FILE_NAME}`,
    instructions: [
      '1. "설정 다운로드" 버튼으로 현재 설정을 파일로 저장',
      `2. 저장된 "${CONFIG_FILE_NAME}" 파일을 프로젝트의 "public/${CONFIG_DIR_NAME}/" 폴더에 복사`,
      '3. 서버 재시작 시 해당 파일에서 설정이 자동으로 로드됨',
      '4. 파일이 없으면 로컬 스토리지 백업을 사용',
      '5. 둘 다 없으면 기본 설정 사용'
    ]
  };
};

export default {
  loadConfigFromFile,
  saveConfigToFile,
  uploadConfigFile,
  autoSaveConfig,
  checkConfigFileExists,
  getConfigInstructions
};